import React, { useState } from 'react';
import styles from './AppointmentsHistory.module.css';
import parts from '../../../appointment/components/moduleCard/moduleParts.module.css';
import AppointmentHistoryItem from './components/appointment-history-item/AppointmentHistoryItem';
import StatTiles from '../../components/StatTiles';
import ModuleCard from '../../../appointment/components/moduleCard/ModuleCard';
import ModuleEmptyState from '../../../appointment/components/moduleCard/ModuleEmptyState';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDateBR, getLastCompleted, timeSince } from '../../utils/appointmentSummary';

/**
 * Seção Agendamentos da ficha: números (realizados, faltas, cancelados pela
 * pessoa, tempo desde o último atendimento) e as listas de próximos e passados.
 * `data` é `appointments` do get_patient.php: `stats`, `upcoming`, `history`.
 */
// Passados aparecem aos poucos: quem tem mil atendimentos esperava mais de
// meio segundo só para montar a lista inteira (PERF-01).
const HISTORY_PAGE = 30;
const HISTORY_STEP = 100;

const AppointmentsHistory = ({ data }) => {
    const stats = data.stats || {};
    const upcoming = data.upcoming || [];
    const history = data.history || [];
    const last = getLastCompleted(history);
    const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE);
    const visibleHistory = history.length > historyLimit ? history.slice(0, historyLimit) : history;
    const hiddenHistory = history.length - visibleHistory.length;

    const tiles = [
        {
            key: 'completed',
            icon: <CheckCircleOutlineIcon />,
            label: 'Realizados',
            value: String(stats.completed || 0),
            accent: 'green',
        },
        {
            key: 'noshow',
            icon: <EventBusyOutlinedIcon />,
            label: 'Faltas',
            value: String(stats.noshow || 0),
            accent: 'pink',
        },
        {
            key: 'canceled',
            icon: <CancelOutlinedIcon />,
            label: 'Cancelados',
            value: String(stats.canceled_by_patient || 0),
            detail: 'Pelo paciente',
            accent: 'amber',
        },
        {
            key: 'since',
            icon: <HourglassBottomOutlinedIcon />,
            label: 'Último atendimento',
            value: last ? `há ${timeSince(last)}` : 'Nunca veio',
            detail: last ? formatDateBR(last) : 'Sem atendimento realizado',
            accent: 'purple',
        },
    ];

    return (
        <ModuleCard
            title="Agendamentos"
            icon={<CalendarMonthOutlinedIcon />}
            description="Próximos e passados"
            accent="teal"
        >
            <StatTiles items={tiles} />

            <div className={styles.lists}>
                <div className={parts.section}>
                    <span className={parts.sectionLabel}>Próximos agendamentos</span>
                    {upcoming.length === 0 ? (
                        <ModuleEmptyState icon={<CalendarMonthOutlinedIcon />} text="Nenhum agendamento marcado." />
                    ) : (
                        <div className={styles.list}>
                            {upcoming.map((appointment, index) => (
                                <AppointmentHistoryItem key={index} data={appointment} />
                            ))}
                        </div>
                    )}
                </div>

                <div className={parts.section}>
                    <span className={parts.sectionLabel}>Agendamentos passados</span>
                    {history.length === 0 ? (
                        <ModuleEmptyState icon={<CalendarMonthOutlinedIcon />} text="Nenhum agendamento passado." />
                    ) : (
                        <div className={styles.list}>
                            {visibleHistory.map((appointment, index) => (
                                <AppointmentHistoryItem key={index} data={appointment} />
                            ))}
                            {hiddenHistory > 0 && (
                                <button
                                    type="button"
                                    className={styles.viewMore}
                                    onClick={() => setHistoryLimit((limit) => limit + HISTORY_STEP)}
                                >
                                    <ExpandMoreIcon fontSize="small" />
                                    Ver mais {hiddenHistory}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </ModuleCard>
    );
};

export default AppointmentsHistory;
